import React, { useMemo, useState } from 'react';
import Bath from '@src/assets/svg/bath';
import Bed from '@src/assets/svg/bed';
import GreenArrowUp from '@src/assets/svg/greenArrowUp';
import Map from '@src/assets/svg/map';
import RedArrowDown from '@src/assets/svg/redArrowDown';
import { propertyCardProps } from '@src/interfaces/components/propertyCard';
import {
  formatCurrency,
  formatNumber,
  formatNumberWithCommas,
  splitAddress,
} from '@src/utils/helper';
import Image from '@src/components/coreUI/image';
import { ZEME_USERS_ROLE } from '@src/constants/configs';
import Icon from '../coreUI/icon';
import Chip from '../coreUI/chip';
import { getUserType } from '@src/utils/localStorage';
import Link from 'next/link';

const PropertyCard: React.FC<propertyCardProps> = (props) => {
  const {
    id,
    role,
    image,
    price,
    updatedPrice,
    address: addressObj,
    bath,
    bedroom,
    squareFootage,
    isEasyApply,
    isMyListing = false,
    isSaved = false,
    handleSaveUnsaveProperty = () => {
      return;
    },
    isExclusive,
    netEffectiveRent,
    leaseConcession,
    leaseMonthsFree,
    leaseTerm,
    brokerFee,
  } = props;

  const range = (updatedPrice || 0) - (price || 0);
  //state
  const [saved, setSaved] = useState<boolean>(false);

  const address = useMemo(() => {
    if (addressObj) return splitAddress(addressObj, isExclusive === false);

    return {
      title: '',
      subTitle: '',
    };
  }, [addressObj, isExclusive]);

  useMemo(() => {
    setSaved(isSaved);
  }, [isSaved]);

  const currentPath =
    typeof window !== 'undefined' ? window.location.pathname : '';
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set('property', id);

  const openModalUrl = `${currentPath}?${searchParams.toString()}`;
  return (
    <div className="relative mx-auto w-full bg-white rounded-lg drop-shadow-thumbnail hover:drop-shadow-md border border-lightertwo/30">
      <Link href={`${openModalUrl}`} scroll={false}>
        <div
          className="relative inline-block w-full transform transition-transform duration-300 ease-in-out cursor-pointer h-full"
          // onClick={openPropertyDetailsModal}
        >
          <div className="rounded-lg h-full flex flex-col">
            <div className="relative flex justify-center overflow-hidden rounded-lg rounded-b-none">
              <div className="w-full transform transition-transform duration-500 ease-in-out hover:scale-110 max-h-[200px] min-h-[200px] bg-gradient-to-r from-gray-lighter to-baseBg">
                <Image
                  src={image !== '' ? image : '/no-image.png'}
                  width="500"
                  height="500"
                  alt="thumbnail title"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>

              <div className="absolute bottom-0 left-0 w-full">
                <div className="bg-gradient-to-b from-black-dark/10 to-black-dark/75 px-2 py-1 text-white text-xs font-semibold shadow-light-color flex flex-col justify-center min-h-[44px]">
                  {/* <p className="line-clamp-1">{name}</p> */}
                  <div className="flex text-sm">
                    {updatedPrice !== null &&
                    updatedPrice > 0 &&
                    updatedPrice !== price ? (
                      <p
                        className={`flex items-center mr-2 text-xs px-1 rounded ${
                          price != null && updatedPrice < price
                            ? 'bg-danger/25 bg-opacity-30 text-danger'
                            : 'bg-success/25 bg-opacity-30 text-success'
                        } `}
                      >
                        <span className="mr-1">
                          {price != null && updatedPrice > price ? (
                            <GreenArrowUp />
                          ) : (
                            <RedArrowDown />
                          )}
                        </span>
                        ${Math.abs(range || 0)}
                      </p>
                    ) : null}
                    <p>
                      {formatCurrency(updatedPrice || price || 0)}
                      /mo
                    </p>
                    {brokerFee === 0 ? (
                      <Chip
                        variant="secondary"
                        extraClasses={`ml-2 text-white bg-gradient-to-r ${
                          getUserType() === ZEME_USERS_ROLE.RENTER
                            ? 'from-secondary-dark to-secondary-dark'
                            : 'from-primary-dark to-primary-dark'
                        }  text-xs !px-[5px] max-h-[16px] !p-0 font-thin !mt-[1px]`}
                      >
                        No fee
                      </Chip>
                    ) : null}
                  </div>
                  {leaseConcession ? (
                    <div>
                      <div className="flex items-center gap-2 text-[10px] font-medium whitespace-nowrap">
                        <div>
                          {formatCurrency(netEffectiveRent || 0)} Net Effective
                          Rent
                        </div>
                        <div className="border-t-0 border-s border-gray-300 h-[10px]"></div>
                        <div>{leaseTerm}-month lease</div>
                        <div className="border-t-0 border-s border-gray-300 h-[10px]"></div>
                        <div className="mr-2">
                          {formatNumber(Number(leaseMonthsFree))} month free
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {isEasyApply ? (
                <span className="absolute left-2.5 top-2.5 z-10 inline-flex select-none bg-white/80 p-1.5 text-black-dark text-xs font-semibold rounded-lg shadow-light-color">
                  Easy Apply
                </span>
              ) : null}
              {role !== ZEME_USERS_ROLE.AGENT ||
              role !== ZEME_USERS_ROLE.LANDLORD ? (
                <span
                  onClick={(
                    event:
                      | React.MouseEvent<HTMLSpanElement, MouseEvent>
                      | undefined
                  ) => {
                    if (!event) return;

                    event.preventDefault();
                    event.stopPropagation();
                    setSaved(!saved);
                    handleSaveUnsaveProperty(id, isSaved);
                  }}
                  className={`${
                    saved && `text-pink-dark`
                  } absolute right-2.5 top-2.5 z-10 inline-flex select-none rounded-lg bg-white/80  px-1 py-1 shadow-light-color`}
                >
                  {saved ? (
                    <Icon
                      iconName="heartfill"
                      iconProps={{ className: 'h-5 w-5 text-pink-dark' }}
                    />
                  ) : (
                    <Icon
                      iconName="heartnotfill"
                      iconProps={{
                        className:
                          'h-5 w-5 text-black-dark hover:text-pink-dark',
                      }}
                    />
                  )}
                </span>
              ) : null}
            </div>

            {/*thumbnail bottom text part */}
            <div className="px-2.5 pb-2.5">
              <div className="flex items-center mt-4">
                <div className="relative w-full">
                  <h2 className="text-sm font-semibold text-black-dark">
                    {address?.title}
                  </h2>
                  <div className="flex items-center justify-between">
                    <p className="mt-1 line-clamp-1 text-xs text-black-light font-medium">
                      {address?.subTitle}
                    </p>
                    {isMyListing && (
                      <p className="text-xs text-primary-dark font-medium bg-primary-dark/10 rounded-lg py-1 px-2">
                        My Listing
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="pb-2 pt-3 flex font-medium text-xs">
                <p className="flex items-center text-gray-dark mr-4">
                  <span className="mr-2 flex items-center justify-center h-5 w-5 fill-current text-pink-dark">
                    <Bed />
                  </span>
                  <span className="mt-0">
                    {bedroom && bedroom > 0 ? `${bedroom} bed` : 'Studio'}
                  </span>
                </p>
                <p className="flex items-center text-gray-dark mr-4">
                  <span className="mr-2 flex items-center justify-center h-5 w-5 fill-current text-pink-dark">
                    <Bath />
                  </span>
                  <span className="mt-0">{bath} ba</span>
                </p>

                {Number(formatNumberWithCommas(squareFootage || 0)) ? (
                  <p className="flex items-center text-gray-dark">
                    <span className="mr-2 flex items-center justify-center h-5 w-5 fill-current text-pink-dark">
                      <Map />
                    </span>
                    <span className="mt-0">
                      {formatNumberWithCommas(squareFootage || 0)} sqft
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default PropertyCard;
